import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, CreditCard, Check, Clock, ArrowUpRight, 
  ArrowDownRight, IndianRupee
} from 'lucide-react';
import { getBalances, getSettlements } from '../lib/api';
import { toast } from 'sonner';

const CURRENCY_SYMBOLS = {
  INR: '₹',
  USD: '$',
  GBP: '£',
  EUR: '€',
  AUD: 'A$',
  CAD: 'C$'
};

const Settlements = () => {
  const [balances, setBalances] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('balances');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [balancesRes, settlementsRes] = await Promise.all([
        getBalances(),
        getSettlements()
      ]);
      setBalances(balancesRes.data);
      setSettlements(settlementsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'INR') => {
    const symbol = CURRENCY_SYMBOLS[currency] || '₹';
    return `${symbol}${amount?.toFixed(2) || '0.00'}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(135deg, #fff7ed 0%, #fef3c7 50%, #fff7ed 100%)'}}>
        <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  const netBalance = (balances?.total_owed || 0) - (balances?.total_owe || 0);

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #fff7ed 0%, #fef3c7 50%, #fff7ed 100%)'}}>
      {/* Background */}
      <div className="fixed inset-0 bg-gradient-mesh pointer-events-none" />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-white/95 backdrop-blur-xl border-b border-orange-100 z-40 flex items-center px-4 lg:px-8 shadow-sm">
        <div className="max-w-4xl mx-auto w-full flex items-center gap-4">
          <Link to="/dashboard" className="p-2 hover:bg-orange-50 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </Link>
          <h1 className="text-xl font-semibold font-['Poppins'] text-slate-800">Settlements</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 px-4 lg:px-8 pb-8 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Net Balance Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/90 backdrop-blur rounded-3xl p-8 mb-8 text-center relative overflow-hidden border border-orange-100 shadow-lg"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-transparent to-amber-50" />
            <div className="relative">
              <p className="text-slate-500 mb-2">Net Balance</p>
              <p className={`text-5xl font-bold font-['Poppins'] ${
                netBalance >= 0 ? 'text-green-600' : 'text-red-500'
              }`}>
                {netBalance >= 0 ? '+' : '-'}{formatCurrency(Math.abs(netBalance))}
              </p>
              <p className="text-slate-500 mt-2">
                {netBalance >= 0 
                  ? "You're in the green! Others owe you money."
                  : "You owe more than you're owed."
                }
              </p>
            </div>
          </motion.div>

          {/* Stats */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="stat-card"
              data-testid="you-owe-card"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-slate-500">You Owe</span>
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <ArrowUpRight className="w-5 h-5 text-red-500" />
                </div>
              </div>
              <p className="text-3xl font-bold text-red-500">
                {formatCurrency(balances?.total_owe)}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                to {balances?.you_owe?.length || 0} people
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="stat-card"
              data-testid="you-are-owed-card"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-slate-500">You're Owed</span>
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <ArrowDownRight className="w-5 h-5 text-green-600" />
                </div>
              </div>
              <p className="text-3xl font-bold text-green-600">
                {formatCurrency(balances?.total_owed)}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                from {balances?.you_are_owed?.length || 0} people
              </p>
            </motion.div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setActiveTab('balances')}
              className={`px-6 py-3 rounded-full font-medium transition-all ${
                activeTab === 'balances'
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                  : 'bg-white text-slate-600 hover:bg-orange-50 border border-orange-100'
              }`}
              data-testid="balances-tab"
            >
              Balances
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-6 py-3 rounded-full font-medium transition-all ${
                activeTab === 'history'
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                  : 'bg-white text-slate-600 hover:bg-orange-50 border border-orange-100'
              }`}
              data-testid="history-tab"
            >
              History
            </button>
          </div>

          {/* Balances Tab */}
          {activeTab === 'balances' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              {/* You Owe */}
              <div className="bg-white/90 backdrop-blur rounded-2xl p-6 border border-orange-100 shadow-lg">
                <h3 className="text-lg font-semibold font-['Poppins'] mb-4 flex items-center gap-2 text-slate-800">
                  <ArrowUpRight className="w-5 h-5 text-red-500" />
                  You Owe
                </h3>
                {balances?.you_owe?.length > 0 ? (
                  <div className="space-y-3">
                    {balances.you_owe.map((person, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 rounded-xl bg-slate-50 hover:bg-red-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white font-medium shadow-md">
                            {person.name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{person.name || person.email}</p>
                            <p className="text-sm text-slate-500">{person.email}</p>
                          </div>
                        </div>
                        <span className="text-lg font-semibold text-red-500">
                          {formatCurrency(person.amount, person.currency)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Check className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
                    <p>You don't owe anyone!</p>
                  </div>
                )}
              </div>

              {/* You're Owed */}
              <div className="bg-white/90 backdrop-blur rounded-2xl p-6 border border-orange-100 shadow-lg">
                <h3 className="text-lg font-semibold font-['Poppins'] mb-4 flex items-center gap-2 text-slate-800">
                  <ArrowDownRight className="w-5 h-5 text-green-600" />
                  You're Owed
                </h3>
                {balances?.you_are_owed?.length > 0 ? (
                  <div className="space-y-3">
                    {balances.you_are_owed.map((person, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 rounded-xl bg-slate-50 hover:bg-green-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-white font-medium shadow-md">
                            {person.name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{person.name || person.email}</p>
                            <p className="text-sm text-slate-500">{person.email}</p>
                          </div>
                        </div>
                        <span className="text-lg font-semibold text-green-600">
                          {formatCurrency(person.amount, person.currency)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <IndianRupee className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No one owes you yet</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white/90 backdrop-blur rounded-2xl p-4 sm:p-6 border border-orange-100 shadow-lg"
            >
              <h3 className="text-lg font-semibold font-['Poppins'] mb-4 text-slate-800">Settlement History</h3>
              {settlements.length > 0 ? (
                <div className="space-y-3">
                  {settlements.map((settlement, index) => (
                    <div
                      key={settlement.settlement_id || index}
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 rounded-xl bg-slate-50 gap-3"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 flex-shrink-0 rounded-full bg-green-100 flex items-center justify-center">
                          <Check className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-800 truncate">
                            {settlement.from_user_name || settlement.from_user_id?.split('@')[0] || 'Payment'} paid
                          </p>
                          <p className="text-sm text-slate-500 truncate">
                            {new Date(settlement.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                      <span className="text-lg font-semibold text-green-600 sm:flex-shrink-0 text-right sm:text-left">
                        {formatCurrency(settlement.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No settlements yet</p>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Settlements;
